import React, { useState, useEffect } from 'react'

const AdjustmentTools = ({ image, onAdjust }) => {
  const [brightness, setBrightness] = useState(0)
  const [contrast, setContrast] = useState(0)
  const [saturation, setSaturation] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [flipH, setFlipH] = useState(false)
  const [flipV, setFlipV] = useState(false)

  const [previewImage, setPreviewImage] = useState(image)

  useEffect(() => {
    applyAdjustments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brightness, contrast, saturation, rotation, flipH, flipV, image])

  const applyAdjustments = () => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      if (rotation === 90 || rotation === 270) {
        canvas.width = img.height
        canvas.height = img.width
      } else {
        canvas.width = img.width
        canvas.height = img.height
      }

      ctx.save()
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate((rotation * Math.PI) / 180)
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1)
      ctx.drawImage(img, -img.width / 2, -img.height / 2)
      ctx.restore()

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        let r = data[i]
        let g = data[i + 1]
        let b = data[i + 2]

        r += brightness
        g += brightness
        b += brightness

        const contrastFactor = (259 * (contrast + 255)) / (255 * (259 - contrast))
        r = contrastFactor * (r - 128) + 128
        g = contrastFactor * (g - 128) + 128
        b = contrastFactor * (b - 128) + 128

        const gray = 0.2989 * r + 0.5870 * g + 0.1140 * b
        r = gray + (r - gray) * saturation
        g = gray + (g - gray) * saturation
        b = gray + (b - gray) * saturation

        data[i] = Math.min(255, Math.max(0, r))
        data[i + 1] = Math.min(255, Math.max(0, g))
        data[i + 2] = Math.min(255, Math.max(0, b))
      }

      ctx.putImageData(imageData, 0, 0)
      setPreviewImage(canvas.toDataURL())
    }

    img.onerror = (e) => {
      if (process.env.NODE_ENV === 'development') console.error('AdjustmentTools: Failed to load image', image, e)
    }

    img.src = image
  }

  const handleApply = () => {
    onAdjust(previewImage)
  }

  const handleReset = () => {
    setBrightness(0)
    setContrast(0)
    setSaturation(1)
    setRotation(0)
    setFlipH(false)
    setFlipV(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-center bg-gray-100 rounded-lg p-4">
        <img
          src={previewImage}
          alt="Preview"
          className="max-h-[700px] max-w-full rounded"
        />
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brightness: {brightness}
          </label>
          <input
            type="range"
            min="-100"
            max="100"
            value={brightness}
            onChange={(e) => setBrightness(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contrast: {contrast}
          </label>
          <input
            type="range"
            min="-100"
            max="100"
            value={contrast}
            onChange={(e) => setContrast(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Saturation: {saturation.toFixed(1)}
          </label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={saturation}
            onChange={(e) => setSaturation(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rotation
          </label>
          <div className="flex gap-2">
            {[0, 90, 180, 270].map((deg) => (
              <button
                key={deg}
                onClick={() => setRotation(deg)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  rotation === deg
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {deg}°
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Flip
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setFlipH(!flipH)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                flipH
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Horizontal
            </button>
            <button
              onClick={() => setFlipV(!flipV)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                flipV
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Vertical
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <button
          onClick={handleReset}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
        >
          Reset
        </button>
        <button
          onClick={handleApply}
          className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
        >
          Apply Adjustments
        </button>
      </div>
    </div>
  )
}

export default AdjustmentTools
